import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Switch, Group, Button, Textarea, NumberInput, Select } from '@mantine/core'
import { isNotEmpty, useForm } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import { IconChevronDown } from '@tabler/icons-react';
import { sectionTypes } from "./constants";
import type { ISection, ICategory } from "./types";
import SearchableSelect from "../../components/common/SearchableSelect";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { uploadFile } from "../../utils/upload";
import Page from "../../components/layout/Page";
import { IPlaylist } from "../playlist/types";
import { useFormStyles } from "../../styles/form";

interface FormValues extends Pick<ISection, 'code' | 'itemId' | 'itemTitle' | 'order' | 'title' | 'type' | 'isActive'> {
    cover: string | FileWithPath | null;
}

interface IParams extends Omit<FormValues, 'cover'> {
    cover?: string;
}

const EditSection = () => {

    const { state: id = null } = useLocation();

    const { classes } = useFormStyles();
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            code: '',
            itemId: '',
            itemTitle: '',
            type: 'category',
            order: 0,
            title: '',
            isActive: false,
            cover: null,
        },

        validate: {
            code: isNotEmpty('Код оруулна уу'),
            title: isNotEmpty('Нэр оруулна уу'),
            type: isNotEmpty('Төрөл сонгоно уу'),
            itemId: isNotEmpty('Холбох item сонгоно уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<ISection>>(`${API_ROUTE}/${id}`);
            form.setValues(data.result);
            form.resetDirty();
        }
        void fetchData();
    }, []);

    React.useEffect(() => {
        form.setFieldValue('itemId', '');
    }, [form.values.type]);

    const categoryParser = (category: ICategory) => {
        return {
            value: category._id,
            label: category.title,
        }
    }

    const playlistParser = (playlist: IPlaylist) => {
        return {
            value: playlist._id,
            label: playlist.title,
        }
    }

    const handleSubmit = async (values: typeof form.values) => {
        let cover: undefined | string = undefined;
        if (values.cover && values.cover instanceof File) {
            cover = await uploadFile(values.cover, 'section' , 'cover');
        }
        const params: IParams = {
            ...values,
            cover
        }
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">

                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            disabled
                            autoComplete='off'
                            label="Код"
                            placeholder="Код оруулна уу"
                            {...form.getInputProps('code')}
                        />

                        <TextInput
                            withAsterisk
                            label="Нэр"
                            placeholder="Нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <Textarea
                            label="Тайлбар"
                            placeholder="Тайлбар оруулна уу"
                            {...form.getInputProps('description')}
                        />

                        <Select
                            withAsterisk
                            label="Төрөл"
                            rightSection={<IconChevronDown size={14} />}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                            data={sectionTypes}
                            {...form.getInputProps('type')}
                        />

                        {
                            form.values.type === 'category'
                                ?   <SearchableSelect<ICategory>
                                    uri={'/api/category/search'}
                                    parser={categoryParser}
                                    selectProps={{
                                        withAsterisk: true,
                                        label: 'Холбосон сэдэв',
                                        ...form.getInputProps('itemId')
                                    }}
                                />
                                :   form.values.type === 'playlist'
                                    ?    <SearchableSelect<IPlaylist>
                                        uri={'/api/playlist/search'}
                                        parser={playlistParser}
                                        selectProps={{
                                            withAsterisk: true,
                                            label: 'Playlist',
                                            ...form.getInputProps('itemId')
                                        }}
                                    />
                                    :   <TextInput
                                        withAsterisk
                                        label='Холбосон бүлгийн дугаар'
                                        placeholder='Холбосон бүлгийн дугаар'
                                        {...form.getInputProps('itemId')}
                                    />
                        }

                        <NumberInput
                            label="Дараалал"
                            placeholder="Дараалал оруулна уу"
                            {...form.getInputProps('order')}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditSection;